import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './empStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const EmpCreate = () => {
    const [employee, setEmployee] = useState({
        IDNumber: '',
        LastName: '',
        FirstName: '',
        MiddleInitial: '',
        Position: '',
        UserID: '',
        DepartmentId: '',
    });

    const [userList, setUserList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    const navigate = useNavigate();
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;


    const handleInputChange = (e) => {
        setEmployee({ ...employee, [e.target.name]: e.target.value });
    };

    const handleUserChange = (user) => {
        setEmployee({
            ...employee,
            UserID: user?.UserID || '',
            FirstName: user?.FirstName || '',
            LastName: user?.LastName || '',
            MiddleInitial: user?.MiddleName || '',
        });
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${apiUrl}/User`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await response.json();
                if (!response.ok) {
                    if (response.status === 401) {
                        // Redirect to login page if unauthorized
                        window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                    } else {
                        // Handle other error cases
                        setErrorMessage('Failed to fetch users data');
                        setSuccessMessage('');
                    }
                    return;
                }


                setUserList(data.User);
            } catch (error) {                
                setErrorMessage('Error fetching users data');
                setSuccessMessage('');
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        const fetchDepartments = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${apiUrl}/Department`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await response.json();
                if (!response.ok) return;

                setDepartmentList(data.Departments);
            } catch (error) {
                setErrorMessage('Error fetching departments');
            }
        };

        fetchDepartments();
    }, []);

    const handleForSubmit = async (e) => {
        e.preventDefault();

        const { IDNumber, LastName, FirstName, MiddleInitial, Position, UserID, DepartmentId } = employee;

        // Create New Employee Object
        const newEmployee = {
            IDNumber,
            LastName,
            FirstName,
            MiddleInitial,
            Position,
            UserID,
            DepartmentId: parseInt(DepartmentId),
        };

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/Employee/Create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newEmployee),
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    // Redirect to login page if unauthorized
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    setErrorMessage(data.Message);
                    setSuccessMessage('');
                    return
                }

            }

            setSuccessMessage('Employee created successfully.');
            setErrorMessage('');

            navigate('/employee-list');
        } catch (error) {
            setErrorMessage('Error creating employee');
            setSuccessMessage('');
        }
    };

    return (
        <div>
            {userAccess.hasAdmin ? (
                <>
                    <h3>Create Employee</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <form onSubmit={handleForSubmit}>
                        <div className='mb-3'>
                            <label htmlFor='idNumber' className='form-label'>ID Number</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="idNumber"
                                name="IDNumber"
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='lastName' className='form-label'>Last Name</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="lastName"
                                name="LastName"
                                value={employee.LastName}
                                readOnly
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='firstName' className='form-label'>First Name</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="firstName"
                                name="FirstName"
                                value={employee.FirstName}
                                readOnly
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="middleInitial" className="form-label">Middle Name</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="middleInitial"
                                name="MiddleInitial"
                                value={employee.MiddleInitial}
                                readOnly
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='departmentId' className='form-label'>Department</label>
                            <select
                                className="form-control custom-width"
                                id="departmentId"
                                name="DepartmentId"
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select a Department---</option>
                                {departmentList.map(dept => (
                                    <option key={dept.Id} value={dept.Id}>
                                        {`${dept.Code} - ${dept.Description}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="position" className="form-label">Position</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="position"
                                name="Position"
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className='mb-3'>
                            <label htmlFor='userId' className='form-label'>User</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="userId"
                                name="UserID"
                                value={employee.UserID}
                                onChange={(e) => handleUserChange(userList[e.target.selectedIndex - 1])}
                                required
                            >
                                <option value="">---Select an user---</option>
                                {userList.map(user => (
                                    <option key={user.UserID} value={user.UserID}>
                                        {`${user.Email}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <Link to="/employee-list" className="btn btn-secondary me-2">Back</Link>
                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                            <button type="submit" className="btn btn-primary">Save</button>
                        )}
                    </form>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default EmpCreate;